/**
 * Chapter 5 Optimized Layout Component
 * 
 * Clean 70/30 split layout matching Chapter 4's design:
 * - 70% viewport height: Video/Image section (top)
 * - 30% viewport height: Text section (bottom)
 * - No scroll needed - everything fits in one viewport
 * - Uses Cloudflare Stream videos with 110% zoom to remove black bars
 */

import { motion } from "motion/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CueInteraction } from "@/components/cue-interaction";
import { Chapter5CueChoice } from "@/components/chapter-5-cue-choice";
import { ForestJourneyMap } from "@/components/forest-journey-map";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { saveBookmark } from "@/lib/bookmark";
import { updateCurrentPosition } from "@/lib/platform-tracking";
import { 
  ArrowLeft, 
  ArrowRight, 
  Home,
  BookOpen,
  Heart
} from "lucide-react";

// TypeScript interface for spread data (makes videoId optional for video placeholder system)
interface SpreadData {
  spread: number;
  section: string;
  videoId?: string; // 🎬 Optional video ID (ready for when videos arrive)
  imageUrl?: string;
  text?: string;
  cue?: string;
  cueText?: string;
}// Chapter 5 spreads data
const chapter5Data: {
  title: string;
  subtitle: string;
  cue: string;
  cueIcon: any;
  cueColor: string;
  bgColor: string;
  spreads: SpreadData[];
} = {
  title: "Chapter 5: The Gratitude Garden",
  subtitle: "Gratitude",
  cue: "Gratitude Leaf",
  cueIcon: Heart,
  cueColor: "text-green-600",
  bgColor: "bg-green-50",
  spreads: [
    {
      spread: 0,
      section: "map",
    },
    {
      spread: 1,
      section: "story",
      videoId: "c8079c38482abf8d4e24e7a7fcd610ee", // ✅ Cloudflare Stream video (Page 1)
      text: "The next morning, while packing up their picnic in Kindness Meadow, Mila found a sparkling leaf tucked into the map. On it was a message: \"Sometimes, the best adventures are the ones we appreciate most.\""
    },
    {
      spread: 2,
      section: "story",
      videoId: "981809217188a486656b923ba8836931", // ✅ Cloudflare Stream video (Page 2)
      text: "Grandpa Tortoise waved from a nearby path, calling, \"Come along, friends! The Gratitude Garden is waiting.\""
    },
    {
      spread: 3,
      section: "story",
      videoId: "7ace36d4e07686b1a71821c8db21970d", // ✅ Cloudflare Stream video (Page 3)
      text: "Mila and her friends followed Grandpa Tortoise to a garden bursting with color. Every branch and bush shimmered with paper leaves, each covered in drawings and words. Animals from all over the forest were adding their own gratitude leaves."
    },
    {
      spread: 4,
      section: "story",
      videoId: "b44e0caa7da796c1025d989b9ebb9b8a", // ✅ Cloudflare Stream video (Page 4)
      text: "Max squinted at the garden and said, \"If I write 'cheese snacks' on every leaf, do you think the trees will start growing cheese?\"\n\nBella giggled. \"Or maybe sunshine-flavored carrots!\"\n\nMila rolled her eyes. \"Let's not turn the garden into a snack shop.\""
    },
    {
      spread: 5,
      section: "story",
      videoId: "cba4063b45916ebbf2041d7967f096cf", // ✅ Cloudflare Stream video (Page 5)
      text: "Grandpa Tortoise smiled. \"In this garden, we remember all the good things—big and small—that make our hearts feel full. Would you like to add your own gratitude leaves?\""
    },
    {
      spread: 6,
      section: "story",
      videoId: "99121505d1a48c2409df1d2c27b80c70", // ✅ Cloudflare Stream video (Page 6)
      text: "Mila thought of her warm nest, her friends, and the adventure they were sharing. Max drew a picture of cheese, and Bella wrote \"sunshine.\""
    },
    {
      spread: 7,
      section: "cue",
      imageUrl: "https://static.step1.dev/c1de4bbdce08d187574c3ce9f5976138", // ✅ Cue page - static image (no video)
      cue: "Gratitude Leaf",
      cueText: "Mila hugs a shiny leaf close and says, \"I'm thankful for adventures with friends. What about you? Let's share one thing we're grateful for!\""
    },
    {
      spread: 8,
      section: "story",
      videoId: "b8757958141f9a2fde1bb1ffdcdcaa91", // ✅ Cloudflare Stream video (Page 8)
      text: "As they hung their leaves, Mila noticed her worries felt lighter and her smile grew bigger."
    }
  ]
};

interface Chapter5OptimizedProps {
  onBackToContents: () => void;
}

export function Chapter5Optimized({ onBackToContents }: Chapter5OptimizedProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Initialize spread from URL parameter or default to 0
  const spreadParam = searchParams.get('spread');
  const initialSpread = spreadParam ? parseInt(spreadParam) : 0;
  const [currentSpread, setCurrentSpread] = useState(initialSpread);
  const [completedCues, setCompletedCues] = useState<Set<string>>(new Set());

  const handleCueComplete = () => {
    setCompletedCues(prev => new Set(prev).add('gratitude-leaf-cue'));
  };

  const totalSpreads = chapter5Data.spreads.length;
  const currentSpreadData = chapter5Data.spreads[currentSpread];
  
  // Reset spread when coming from bookmark
  useEffect(() => {
    const spreadParam = searchParams.get('spread');
    const targetSpread = spreadParam ? parseInt(spreadParam) : 0;
    setCurrentSpread(targetSpread);
  }, [searchParams]);
  
  // Save bookmark when spread changes
  useEffect(() => {
    saveBookmark(5, currentSpread, chapter5Data.title);
  }, [currentSpread]);

  // Update platform tracking when position changes
  useEffect(() => {
    updateCurrentPosition(5, currentSpread);
  }, [currentSpread]);

  // Send analytics to PostgreSQL using new API
  useEffect(() => {
    const sendAnalyticsAsync = async () => {
      try {
        const { sendAnalytics } = await import('@/lib/analytics/client');
        await sendAnalytics(5, currentSpread + 1);
        console.log(`✅ Enhanced analytics sent to PostgreSQL - Chapter 5, Spread ${currentSpread + 1}`);
      } catch (error) {
        console.warn('Failed to send enhanced analytics:', error);
      }
    };
    sendAnalyticsAsync();
  }, [currentSpread]);

  const handleNextSpread = useCallback(() => {
    if (currentSpread < totalSpreads - 1) {
      setCurrentSpread(currentSpread + 1);
    } else {
      // Go to Activities page after the last story spread
      navigate("/book/chapter/5/activity");
    }
  }, [currentSpread, totalSpreads, navigate]);

  const handlePrevSpread = useCallback(() => {
    if (currentSpread > 0) {
      setCurrentSpread(currentSpread - 1);
    } else {
      navigate("/book/contents");
    }
  }, [currentSpread, navigate]);

  // Enhanced Activities button tracking with proper useCallback
  const handleActivities = useCallback(async () => {
    try {
      const { trackButton, sendAnalytics } = await import('@/lib/analytics/client');
      
      // Track the specific navigation interaction
      trackButton('activities_button', {
        navigation_source: 'chapter_nav',
        position: { x: 0, y: 0 } // Top right nav bar
      });
      
      // Send enhanced analytics immediately
      const chapterNumber = location.pathname.includes('chapter/4') ? 4 : 
                           location.pathname.includes('chapter/5') ? 5 : 6;
      
      await sendAnalytics(chapterNumber, currentSpread + 1);
      
      console.log(`📊 Enhanced Activities button tracking:`, {
        chapter: chapterNumber,
        spread: currentSpread + 1,
        buttonType: 'activities_button',
        navigationSource: 'chapter_nav'
      });
    } catch (error) {
      console.warn('Failed to track activities button:', error);
    }
    
    // Navigate to appropriate chapter activity
    const chapterNumber = location.pathname.includes('chapter/4') ? 4 : 
                         location.pathname.includes('chapter/5') ? 5 : 6;
    navigate(`/book/chapter/${chapterNumber}/activity`);
  }, [navigate, currentSpread, location.pathname]);

  const renderVisual = (videoId?: string, imageUrl?: string) => {
    // Prioritize video if available, otherwise use image
    if (videoId) {
      return (
        /* CLOUDFLARE STREAM VIDEO */
        <iframe
          key={videoId} // Force re-mount on video change
          src={`https://iframe.videodelivery.net/${videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
          style={{ 
            border: 'none', 
            width: '110%',  // 110% zoom (removes black bars - ADJUSTABLE)
            height: '110%', // 110% zoom
            position: 'relative',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none'
          }}
          allow="autoplay; encrypted-media"
          title={`Chapter Video`}
        />
      );
    }
    
    if (imageUrl) {
      return (
        <div className="h-full flex items-center justify-center overflow-hidden">
          <motion.img
            src={imageUrl}
            alt={`Chapter Spread`}
            className="w-full h-full object-contain mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50">
      
      {/* COMPACT HEADER */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-green-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Left: Back + Chapter Title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-forest-600 to-golden-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-forest-800">Brave Things Books</div>
              </div>
            </div>
            <div className="text-forest-400">•</div>
            <Button
              onClick={onBackToContents}
              variant="ghost"
              size="sm"
              className="text-forest-700 px-2 py-1 h-8"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Contents
            </Button>
            <div className="text-forest-400">•</div>
            <h1 className="text-lg font-bold text-forest-700">
              Chapter 5: The Gratitude Garden <span className="text-sm font-normal text-forest-600">• Gratitude</span>
            </h1>
          </div>
          
          {/* Right: Badge + Activities */}
          <div className="flex items-center gap-2">
            <Badge 
              className="text-xs px-2 py-1 border bg-white/50 backdrop-blur-sm" 
              style={{ 
                color: '#5D4E37', 
                borderColor: '#E6D2AA' 
              }}
            >
              💖 Gratitude Leaf
            </Badge>
            
            <Button
              onClick={handleActivities}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-1 h-8 border-green-300 text-green-700 bg-white/20"
            >
              🎯 Activities
            </Button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {currentSpreadData?.section === "map" ? (
          /* FULL-SCREEN MAP PAGE (Spread 0) */
          <div className="fixed inset-0 z-50">
            <ForestJourneyMap currentChapter={5} />
          </div>
        ) : currentSpreadData?.section === "cue" ? (
          /* CUE PAGE - FULL PAGE LAYOUT (unchanged - special case) */
          <div className="flex-1 relative overflow-hidden">
            
            {/* Background Image */}
            <div className="absolute inset-0">
              <motion.img
                src={currentSpreadData.imageUrl}
                alt={`Chapter 5 Spread ${currentSpread + 1}`}
                className="w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-green-400/40 via-green-300/60 to-green-500/70"></div>
            </div>

            {/* Main Cue Box - Centered */}
            <div className="relative z-10 h-full flex items-center justify-center p-8">
              <motion.div 
                className="bg-cream-50 border-4 border-green-400 rounded-3xl p-12 text-center mx-auto max-w-2xl"
                style={{ 
                  backgroundColor: '#FDF6E3',
                  borderColor: '#22C55E'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {/* Celebratory Emojis at Top */}
                <div className="text-5xl mb-8">
                  💚 ✨ 💚
                </div>
                
                {/* Magic Cue Found Title */}
                <h2 className="text-4xl font-bold mb-10" style={{ color: '#166534' }}>
                  ✨ Magic Cue Found! ✨
                </h2>
                
                {/* Instruction Text */}
                <div className="text-3xl leading-relaxed font-medium mb-6" style={{ color: '#15803D' }}>
                  Click the glowing Gratitude Leaf in the corner to try the activity!
                </div>
              </motion.div>
            </div>

            {/* Cue Interaction */}
            <div className="absolute top-8 right-8 z-20">
              <Chapter5CueChoice
                onComplete={handleCueComplete}
                isCompleted={completedCues.has('gratitude-leaf-cue')}
              />
            </div>
          </div>
        ) : (
          /* ✅ STORY PAGES - CLEAN 70/30 SPLIT LAYOUT (matches Chapter 4) */
          <>
            {/* TOP: VIDEO SECTION - 70% of screen height */}
            <div className="h-[70%] bg-transparent overflow-hidden relative pt-6">
              <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br max-w-5xl mx-auto">
                {currentSpreadData?.videoId ? (
                  /* CLOUDFLARE STREAM VIDEO */
                  <iframe
                    key={currentSpreadData.videoId} // Force re-mount on video change
                    src={`https://iframe.videodelivery.net/${currentSpreadData.videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
                    style={{ 
                      border: 'none', 
                      width: '130%',  // 130% zoom (removes black bars for Chapter 5)
                      height: '130%', // 130% zoom
                      position: 'relative',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                    allow="autoplay; encrypted-media"
                    title={`Chapter 5 Video`}
                  />
                ) : currentSpreadData?.imageUrl ? (
                  <div className="h-full flex items-center justify-center overflow-hidden">
                    <motion.img
                      src={currentSpreadData.imageUrl}
                      alt={`Chapter 5 Spread ${currentSpread + 1}`}
                      className="w-full h-full object-contain mx-auto"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            
            {/* BOTTOM: TEXT SECTION - 30% of screen height */}
            <div className="h-[30%] flex flex-col px-8 py-6">
              <div className="flex-1 flex items-center">
                <div className="text-xl leading-relaxed text-gray-900 max-w-5xl mx-auto" style={{ textAlign: 'left' }}>
                  {currentSpreadData?.text?.split('\n\n').map((paragraph, index) => (
                    <p key={index} className={index > 0 ? 'mb-7' : ''}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Minimal Navigation Controls */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex justify-between items-center py-0 px-2">
          {/* Previous Button */}
          <Button
            onClick={handlePrevSpread}
            disabled={currentSpread === 0}
            variant="ghost"
            size="sm"
            className="text-forest-600 disabled:opacity-50 px-2 py-0 h-6"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Prev
          </Button>

          {/* Chapter Progress with Dots */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-forest-600">Chapter Progress</span>
            <div className="flex gap-1">
              {chapter5Data.spreads.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSpread(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentSpread
                      ? 'bg-forest-500'
                      : 'bg-forest-200'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-forest-600">{currentSpread} of {chapter5Data.spreads.length - 1}</span>
          </div>

          {/* Next Button */}
          <Button
            onClick={handleNextSpread}
            variant="ghost"
            size="sm"
            className="text-forest-600 px-2 py-0 h-6"
          >
            {currentSpread === totalSpreads - 1 ? "Activities" : "Next"}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>

    </div>
  );
}
